import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import type { GridPage, GridRequest } from '../../ui';
import {
  AccessCatalog,
  AccessMe,
  AccessTemplate,
  ApplyBreakdownRow,
  ApplyDryRunResult,
  ApplyOutcome,
  ApplyResult,
  CatalogModule,
  CatalogResource,
  CatalogResourcePayload,
  CatalogSection,
  CatalogLayoutState,
  LayoutDraftPayload,
  MenuIconCatalog,
  ResourceDraftPayload,
  CompanyModules,
  CompanyRole,
  CompanyTemplateRow,
  CreateCompanyRolePayload,
  CreateTemplatePayload,
  DeleteResourceResult,
  FALLBACK_MODULE_ID,
  isProductRole,
  PRODUCT_ROLE_LABEL,
  PRODUCT_ROLES,
  ProductRole,
  PublishResult,
  TemplateHistoryEntry,
  TemplateHistoryRow,
  UpdateTemplatePayload,
  UserAccess,
  UserAccessHistoryEntry,
  UserAccessPayload,
  VERB_LABELS,
} from '../models/access.model';

/**
 * Permisos de la web — endpoints del backoffice (contrato §5 y §8.4, montado
 * en /v1/admin; lecturas con `access.read`, escrituras con `access.manage`)
 * más GET /access/me para conocer las claves de staff de la sesión.
 *
 * Las respuestas se normalizan aquí (ids `_id`/`id`, conteos, arrays
 * ausentes) para que las vistas trabajen siempre con la forma del contrato.
 */
@Injectable({ providedIn: 'root' })
export class AccessAdminService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private readonly admin = `${environment.apiUrl}admin/`;

  /**
   * Caché del catálogo por alcance: '' = catálogo completo (plataforma +
   * exclusivos de todas las empresas); un id = catálogo de esa empresa.
   * Cualquier escritura de recursos lo invalida entero.
   */
  private readonly catalogs = new Map<string, Observable<AccessCatalog>>();
  private menuIcons$: Observable<MenuIconCatalog> | null = null;

  // ── Sesión ────────────────────────────────────────────────────────────

  getMe(): Observable<AccessMe> {
    return this.http
      .get<ApiResponse<AccessMe>>(`${this.apiUrl}access/me`)
      .pipe(map((res) => normalizeMe(res.data)));
  }

  // ── Catálogo ──────────────────────────────────────────────────────────

  /**
   * Catálogo completo (GET /admin/access/catalog sin `companyId`): módulos de
   * plataforma y exclusivos de todas las empresas, cada uno con `companyId` y
   * `companyName`. Lo usa la pantalla «Catálogo de módulos».
   */
  getCatalog(force = false): Observable<AccessCatalog> {
    return this.cachedCatalog('', force);
  }

  /** Sólo módulos de plataforma: lo único que admiten las plantillas base. */
  getPlatformCatalog(force = false): Observable<AccessCatalog> {
    return this.getCatalog(force).pipe(map(platformOnly));
  }

  /**
   * Catálogo de una empresa (`?companyId=`): plataforma + sus exclusivos. Se
   * filtra también aquí por si el API devolviera exclusivos de otra empresa.
   */
  getCompanyCatalog(companyId: string, force = false): Observable<AccessCatalog> {
    return this.cachedCatalog(companyId, force).pipe(
      map((catalog) => restrictToCompany(catalog, companyId)),
    );
  }

  private cachedCatalog(companyId: string, force: boolean): Observable<AccessCatalog> {
    let catalog$ = this.catalogs.get(companyId);
    if (force || !catalog$) {
      const params = companyId ? new HttpParams().set('companyId', companyId) : undefined;
      catalog$ = this.http
        .get<ApiResponse<AccessCatalog>>(`${this.admin}access/catalog`, { params })
        .pipe(
          map((res) => normalizeCatalog(res.data)),
          catchError((err) => {
            this.catalogs.delete(companyId);
            return throwError(() => err);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
      this.catalogs.set(companyId, catalog$);
    }
    return catalog$;
  }

  /** Olvida todos los catálogos en caché (tras crear, editar o borrar un recurso). */
  invalidateCatalog(): void {
    this.catalogs.clear();
  }

  /** Con `companyId` crea un módulo exclusivo de esa empresa; sin él, de plataforma. */
  createResource(payload: CatalogResourcePayload): Observable<unknown> {
    return this.http
      .post<ApiResponse<unknown>>(`${this.admin}access/catalog/resources`, payload)
      .pipe(
        map((res) => res.data),
        tap(() => this.invalidateCatalog()),
      );
  }

  updateResource(resourceId: string, payload: CatalogResourcePayload): Observable<unknown> {
    return this.http
      .put<ApiResponse<unknown>>(
        `${this.admin}access/catalog/resources/${encodeURIComponent(resourceId)}`,
        payload,
      )
      .pipe(
        map((res) => res.data),
        tap(() => this.invalidateCatalog()),
      );
  }

  deleteResource(resourceId: string): Observable<DeleteResourceResult> {
    return this.http
      .delete<ApiResponse<DeleteResourceResult>>(
        `${this.admin}access/catalog/resources/${encodeURIComponent(resourceId)}`,
      )
      .pipe(
        map((res) => res.data),
        tap(() => this.invalidateCatalog()),
      );
  }

  // ── Borrador del catálogo y publicar (access-catalog-layout.spec.md) ──
  // Lo que se arma en «Organizar» y lo que se edita de cada módulo queda como borrador: nadie lo ve
  // hasta publicar. Publicar sube todo junto y la versión del catálogo.

  /** Deja en el borrador cómo queda un módulo editado (igual a lo publicado, lo saca). */
  saveResourceDraft(
    resourceId: string,
    payload: ResourceDraftPayload,
  ): Observable<CatalogLayoutState> {
    return this.http
      .put<ApiResponse<CatalogLayoutState>>(
        `${this.admin}access/catalog/resources/${encodeURIComponent(resourceId)}/draft`,
        payload,
      )
      .pipe(map((res) => res.data));
  }

  /** Saca del borrador lo editado de un módulo. */
  discardResourceDraft(resourceId: string): Observable<CatalogLayoutState> {
    return this.http
      .delete<ApiResponse<CatalogLayoutState>>(
        `${this.admin}access/catalog/resources/${encodeURIComponent(resourceId)}/draft`,
      )
      .pipe(map((res) => res.data));
  }

  getCatalogLayout(): Observable<CatalogLayoutState> {
    return this.http
      .get<ApiResponse<CatalogLayoutState>>(`${this.admin}access/catalog/layout`)
      .pipe(map((res) => res.data));
  }

  saveCatalogLayoutDraft(payload: LayoutDraftPayload): Observable<CatalogLayoutState> {
    return this.http
      .put<ApiResponse<CatalogLayoutState>>(`${this.admin}access/catalog/layout/draft`, payload)
      .pipe(map((res) => res.data));
  }

  discardCatalogLayoutDraft(): Observable<CatalogLayoutState> {
    return this.http
      .delete<ApiResponse<CatalogLayoutState>>(`${this.admin}access/catalog/layout/draft`)
      .pipe(map((res) => res.data));
  }

  publishCatalogLayout(): Observable<CatalogLayoutState> {
    return this.http
      .post<ApiResponse<CatalogLayoutState>>(`${this.admin}access/catalog/layout/publish`, {})
      .pipe(
        map((res) => res.data),
        tap(() => this.invalidateCatalog()),
      );
  }

  /** Íconos que se pueden elegir para el menú (GET /reference/menu-icons, público y cacheable). */
  getMenuIcons(): Observable<MenuIconCatalog> {
    if (!this.menuIcons$) {
      this.menuIcons$ = this.http
        .get<ApiResponse<MenuIconCatalog>>(`${environment.apiUrl}reference/menu-icons`)
        .pipe(
          map((res) => ({
            font: String(res.data?.font ?? ''),
            icons: Array.isArray(res.data?.icons) ? res.data.icons.map(String) : [],
          })),
          catchError((error) => {
            this.menuIcons$ = null;
            return throwError(() => error);
          }),
          shareReplay(1),
        );
    }
    return this.menuIcons$;
  }

  // ── Módulos por empresa ───────────────────────────────────────────────

  getCompanyModules(companyId: string): Observable<CompanyModules> {
    return this.http
      .get<ApiResponse<CompanyModules>>(`${this.admin}companies/${companyId}/modules`)
      .pipe(
        map((res) => ({
          disabledModules: strings(res.data?.disabledModules),
          exclusive: (Array.isArray(res.data?.exclusive) ? (res.data.exclusive as unknown[]) : [])
            .map(normalizeResource)
            .filter((resource) => resource.resourceId),
        })),
      );
  }

  updateCompanyModules(companyId: string, disabledModules: string[]): Observable<string[]> {
    return this.http
      .put<ApiResponse<{ disabledModules: string[] }>>(
        `${this.admin}companies/${companyId}/modules`,
        { disabledModules },
      )
      .pipe(map((res) => strings(res.data?.disabledModules)));
  }

  // ── Plantillas por empresa ────────────────────────────────────────────

  /** Todos los roles de la empresa: los 5 del sistema y luego los propios. */
  listCompanyTemplates(companyId: string): Observable<CompanyTemplateRow[]> {
    return this.http
      .get<ApiResponse<unknown>>(`${this.admin}companies/${companyId}/access-templates`)
      .pipe(map((res) => normalizeRoleRows(res.data, true)));
  }

  /**
   * Personas con un rol propio vigente en la empresa (una sola llamada para
   * la columna «Rol» de Usuarios). Los que caen al rol del sistema no vienen.
   */
  listRoleAssignments(
    companyId: string,
  ): Observable<Array<{ userId: string; roleKey: string; name: string; role: string }>> {
    return this.http
      .get<ApiResponse<Array<{ userId: string; roleKey: string; name: string; role: string }>>>(
        `${this.admin}companies/${companyId}/role-assignments`,
      )
      .pipe(map((res) => (Array.isArray(res.data) ? res.data : [])));
  }

  /** Crea un rol propio (409 `ROLE_KEY_TAKEN` si el nombre genera una clave tomada). */
  createCompanyRole(
    companyId: string,
    payload: CreateCompanyRolePayload,
  ): Observable<AccessTemplate> {
    return this.http
      .post<ApiResponse<unknown>>(`${this.admin}companies/${companyId}/access-templates`, payload)
      .pipe(map((res) => normalizeTemplate(res.data)));
  }

  /** Crea la plantilla que faltaba de un rol del sistema (sólo `role`). */
  createCompanyTemplate(
    companyId: string,
    payload: CreateTemplatePayload,
  ): Observable<AccessTemplate> {
    return this.http
      .post<ApiResponse<unknown>>(`${this.admin}companies/${companyId}/access-templates`, payload)
      .pipe(map((res) => normalizeTemplate(res.data)));
  }

  // ── Plantillas de plataforma (company: null) ──────────────────────────

  /** Plantillas base: sólo los 5 roles del sistema. */
  listDefaultTemplates(): Observable<CompanyTemplateRow[]> {
    return this.http
      .get<ApiResponse<unknown>>(`${this.admin}access-templates/defaults`)
      .pipe(map((res) => normalizeRoleRows(res.data, false)));
  }

  createDefaultTemplate(payload: CreateTemplatePayload): Observable<AccessTemplate> {
    return this.http
      .post<ApiResponse<unknown>>(`${this.admin}access-templates/defaults`, payload)
      .pipe(map((res) => normalizeTemplate(res.data)));
  }

  // ── Plantilla (cualquiera) ────────────────────────────────────────────

  getTemplate(id: string): Observable<AccessTemplate> {
    return this.http
      .get<ApiResponse<unknown>>(`${this.admin}access-templates/${id}`)
      .pipe(map((res) => normalizeTemplate(res.data)));
  }

  updateTemplate(id: string, payload: UpdateTemplatePayload): Observable<AccessTemplate> {
    return this.http
      .put<ApiResponse<unknown>>(`${this.admin}access-templates/${id}`, payload)
      .pipe(map((res) => normalizeTemplate(res.data)));
  }

  /** Renombra un rol (del sistema o propio). No toca permisos ni estado. */
  renameTemplate(id: string, name: string | null): Observable<AccessTemplate> {
    return this.updateTemplate(id, { name });
  }

  /**
   * Borra un rol propio sin personas asignadas. Rol del sistema → 409
   * `SYSTEM_ROLE_LOCKED`; con asignados → 409 `ROLE_IN_USE` (`details.assigned`).
   */
  deleteTemplate(id: string): Observable<unknown> {
    return this.http
      .delete<ApiResponse<unknown>>(`${this.admin}access-templates/${id}`)
      .pipe(map((res) => res.data));
  }

  /** `dryRun: true` calcula el diff sin publicar; `false` publica. */
  publishTemplate(id: string, dryRun: boolean): Observable<PublishResult> {
    return this.http
      .post<ApiResponse<PublishResult>>(`${this.admin}access-templates/${id}/publish`, { dryRun })
      .pipe(
        map((res) => ({
          diff: {
            gained: strings(res.data?.diff?.gained),
            lost: strings(res.data?.diff?.lost),
          },
          affectedUsers: count(res.data?.affectedUsers),
          version: Number(res.data?.version ?? 0),
          published: Boolean(res.data?.published),
        })),
      );
  }

  /**
   * Una página del historial con el contrato de grilla: la API ordena sobre todas las versiones
   * (`q` con el orden; `page` y `limit`) y devuelve la página con su total (`meta.total`).
   */
  getTemplateHistoryPage(id: string, request: GridRequest): Observable<GridPage> {
    const params = new HttpParams()
      .set('page', request.page)
      .set('limit', request.limit)
      .set('q', request.q);
    return this.http
      .get<ApiResponse<TemplateHistoryRow[]>>(`${this.admin}access-templates/${id}/history`, {
        params,
      })
      .pipe(map((res) => ({ data: Array.isArray(res.data) ? res.data : [], meta: res.meta })));
  }

  getTemplateHistory(id: string): Observable<TemplateHistoryEntry[]> {
    return this.http
      .get<ApiResponse<TemplateHistoryEntry[]>>(`${this.admin}access-templates/${id}/history`)
      .pipe(
        map((res) =>
          (Array.isArray(res.data) ? res.data : []).map((entry) => ({
            ...entry,
            gained: strings(entry.gained),
            lost: strings(entry.lost),
          })),
        ),
      );
  }

  /** Paso 1 obligatorio de «Aplicar a otras empresas»: simula y devuelve la huella. */
  applyTemplateDryRun(id: string, companyIds: string[]): Observable<ApplyDryRunResult> {
    return this.http
      .post<ApiResponse<ApplyDryRunResult>>(`${this.admin}access-templates/${id}/apply`, {
        companyIds,
        dryRun: true,
      })
      .pipe(
        map((res) => ({
          fingerprint: String(res.data?.fingerprint ?? ''),
          breakdown: (Array.isArray(res.data?.breakdown) ? res.data.breakdown : []).map(
            normalizeBreakdownRow,
          ),
          role: res.data?.role,
          version: res.data?.version,
        })),
      );
  }

  /** Paso 2: aplica con la huella del dry-run (409 `DRY_RUN_STALE` si cambió algo). */
  applyTemplate(id: string, companyIds: string[], fingerprint: string): Observable<ApplyResult> {
    return this.http
      .post<ApiResponse<ApplyResult>>(`${this.admin}access-templates/${id}/apply`, {
        companyIds,
        dryRun: false,
        fingerprint,
      })
      .pipe(
        map((res) => ({
          applied: (Array.isArray(res.data?.applied) ? res.data.applied : []).map(
            (row): ApplyOutcome => ({
              ...normalizeBreakdownRow(row),
              status: row.status ?? 'applied',
              templateId: row.templateId ?? null,
              version: row.version,
            }),
          ),
          role: res.data?.role,
          version: res.data?.version,
        })),
      );
  }

  // ── Roles ─────────────────────────────────────────────────────────────

  /** Rol de una relación laboral (ficha Employee). Sube `sessionVersion` del usuario. */
  setEmployeeRole(employeeId: string, role: ProductRole): Observable<unknown> {
    return this.http
      .put<ApiResponse<unknown>>(`${this.admin}employees/${employeeId}/role`, { role })
      .pipe(map((res) => res.data));
  }

  /** Rol de una cuenta administrativa (User). Sube `sessionVersion`. */
  setUserRole(userId: string, role: ProductRole): Observable<unknown> {
    return this.http
      .put<ApiResponse<unknown>>(`${this.admin}users/${userId}/role`, { role })
      .pipe(map((res) => res.data));
  }

  // ── Ajustes por persona ───────────────────────────────────────────────

  getUserAccess(userId: string, companyId: string): Observable<UserAccess> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http
      .get<ApiResponse<UserAccess>>(`${this.admin}users/${userId}/access`, { params })
      .pipe(map((res) => normalizeUserAccess(res.data)));
  }

  updateUserAccess(userId: string, payload: UserAccessPayload): Observable<UserAccess> {
    return this.http
      .put<ApiResponse<UserAccess>>(`${this.admin}users/${userId}/access`, payload)
      .pipe(map((res) => normalizeUserAccess(res.data)));
  }

  getUserAccessHistory(userId: string, companyId: string): Observable<UserAccessHistoryEntry[]> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http
      .get<ApiResponse<UserAccessHistoryEntry[]>>(`${this.admin}users/${userId}/access/history`, {
        params,
      })
      .pipe(map((res) => (Array.isArray(res.data) ? res.data : [])));
  }
}

// ── Normalizadores ─────────────────────────────────────────────────────

type Loose = Record<string, unknown>;

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** Conteo tolerante: número, arreglo (largo) u objeto `{ total }`. */
export function count(value: unknown): number {
  if (typeof value === 'number') return value;
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object' && typeof (value as Loose)['total'] === 'number') {
    return (value as Loose)['total'] as number;
  }
  return 0;
}

function refIdOf(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const obj = value as { id?: unknown; _id?: unknown };
  return String(obj.id ?? obj._id ?? '');
}

function normalizeMe(raw: AccessMe | null | undefined): AccessMe {
  return {
    principal: raw?.principal ?? 'product',
    permissions: strings(raw?.permissions),
    staff: strings(raw?.staff),
    // Textos de las claves de staff (support-access.spec.md §1.2.1); sin ellos se muestra la clave
    staffKeys: Array.isArray(raw?.staffKeys)
      ? raw!
          .staffKeys!.filter((info) => info && typeof info.key === 'string')
          .map((info) => ({
            key: info.key,
            label: String(info.label ?? info.key),
            area: String(info.area ?? ''),
            areaLabel: info.areaLabel ?? null,
            verb: info.verb === 'manage' ? 'manage' : 'read',
            verbLabel: String(info.verbLabel ?? ''),
            granted: info.granted === true,
          }))
      : [],
    company: raw?.company ?? null,
    role: raw?.role ?? null,
    accessRole: raw?.accessRole ?? null,
    disabledModules: strings(raw?.disabledModules),
    catalogVersion: Number(raw?.catalogVersion ?? 0),
    accessVersion: Number(raw?.accessVersion ?? 0),
  };
}

function normalizeResource(input: unknown): CatalogResource {
  // Tolerante: un exclusivo puede venir sólo como id.
  const raw: Loose = typeof input === 'string' ? { resourceId: input } : ((input ?? {}) as Loose);
  const resourceId = String(raw['resourceId'] ?? '');
  const rawVerbs = Array.isArray(raw['verbs']) ? (raw['verbs'] as unknown[]) : [];
  const rawSpecials = Array.isArray(raw['specials']) ? (raw['specials'] as unknown[]) : [];
  return {
    resourceId,
    label: String(raw['label'] ?? resourceId),
    icon: (raw['icon'] as string) ?? null,
    // Tolerante: el verbo puede venir como string (forma de la base) o como objeto.
    verbs: rawVerbs.map((verb) => {
      const obj: Loose = typeof verb === 'string' ? { id: verb } : (verb as Loose);
      const id = String(obj['id']);
      return {
        id,
        label: String(obj['label'] ?? VERB_LABELS[id as keyof typeof VERB_LABELS] ?? id),
        key: String(obj['key'] ?? `${resourceId}.${id}`),
        enforced: obj['enforced'] !== false,
      };
    }),
    specials: rawSpecials.map((special) => {
      const obj = special as Loose;
      const id = String(obj['id']);
      return {
        id,
        label: String(obj['label'] ?? id),
        key: String(obj['key'] ?? `${resourceId}.${id}`),
        enforced: obj['enforced'] !== false,
        icon: (obj['icon'] as string) ?? null,
        description: (obj['description'] as string) ?? null,
      };
    }),
    requires: (raw['requires'] as string) || null,
    sensitive: Boolean(raw['sensitive']),
    order: Number(raw['order'] ?? 0),
    source: raw['source'] === 'seed' ? 'seed' : 'custom',
    companyId: raw['companyId'] ? refIdOf(raw['companyId']) || null : null,
    companyName: companyNameOf(raw['companyName'] ?? raw['companyId']),
  };
}

/** Nombre de empresa: string o subdocumento populado (`businessName`/`tradeName`). */
function companyNameOf(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') return /^[a-f0-9]{24}$/i.test(value) ? null : value;
  const obj = value as Loose;
  const name = obj['name'] ?? obj['businessName'] ?? obj['tradeName'];
  return typeof name === 'string' && name ? name : null;
}

/** Copia del catálogo quedándose sólo con los recursos que cumplen `keep`. */
function filterCatalog(
  catalog: AccessCatalog,
  keep: (resource: CatalogResource) => boolean,
): AccessCatalog {
  return {
    ...catalog,
    sections: catalog.sections.map((section) => ({
      ...section,
      resources: section.resources.filter(keep),
    })),
  };
}

/** Sólo módulos de plataforma (`companyId: null`). */
export function platformOnly(catalog: AccessCatalog): AccessCatalog {
  return filterCatalog(catalog, (resource) => !resource.companyId);
}

/** Catálogo de una empresa: plataforma + exclusivos de esa empresa. */
export function restrictToCompany(catalog: AccessCatalog, companyId: string): AccessCatalog {
  return filterCatalog(
    catalog,
    (resource) => !resource.companyId || resource.companyId === companyId,
  );
}

/**
 * Las secciones se conservan EN EL ORDEN QUE LAS ENTREGA EL API (el del menú
 * de la web, contrato §9) y los módulos de cada una por `order` (orden
 * estable: a igual `order` queda el del API). Nada se reordena por nombre.
 */
function normalizeCatalog(raw: AccessCatalog): AccessCatalog {
  const sections: CatalogSection[] = (Array.isArray(raw?.sections) ? raw.sections : []).map(
    (section) => ({
      id: String(section.id ?? section.label),
      label: String(section.label ?? section.id),
      // Sin `module` (API anterior): fuera del Módulo DT. Nada de listas locales.
      module: section.module ? String(section.module) : FALLBACK_MODULE_ID,
      icon: section.icon ? String(section.icon) : null,
      order: typeof section.order === 'number' ? section.order : null,
      source: section.source === 'seed' ? 'seed' : 'custom',
      resources: (Array.isArray(section.resources) ? (section.resources as unknown[]) : [])
        .map(normalizeResource)
        .sort((a, b) => a.order - b.order),
    }),
  );
  const modules: CatalogModule[] = (Array.isArray(raw?.modules) ? raw.modules : [])
    .filter((module) => module && module.id)
    .map((module) => ({
      id: String(module.id),
      label: String(module.label ?? module.id),
      description: module.description ? String(module.description) : null,
      order: Number(module.order ?? 0),
      source: module.source === 'custom' ? 'custom' : 'seed',
    }));
  const rawDefaults = ((raw as unknown as Loose)?.['roleDefaults'] ?? {}) as Loose;
  const roleDefaults: AccessCatalog['roleDefaults'] = {};
  Object.entries(rawDefaults).forEach(([role, keys]) => {
    if (isProductRole(role)) roleDefaults[role] = strings(keys);
  });
  return {
    surface: raw?.surface ?? 'web',
    version: Number(raw?.version ?? 0),
    hash: raw?.hash ?? null,
    source: raw?.source === 'seed' ? 'seed' : 'db',
    commonVerbs: Array.isArray(raw?.commonVerbs) ? raw.commonVerbs : [],
    modules,
    sections,
    roleDefaults,
  };
}

export function normalizeTemplate(raw: unknown): AccessTemplate {
  // GET /access-templates/:id puede venir como `{ ...plantilla, affectedUsers }`
  // o como `{ template, affectedUsers }`.
  const outer = (raw ?? {}) as Loose;
  const tpl = (
    outer['template'] && typeof outer['template'] === 'object' ? outer['template'] : outer
  ) as Loose;
  const affected = outer['affectedUsers'] ?? tpl['affectedUsers'];
  const role = (isProductRole(tpl['role']) ? tpl['role'] : 'worker') as ProductRole;
  // Plantillas anteriores a los roles propios no traen key/system: son del sistema.
  const key = typeof tpl['key'] === 'string' && tpl['key'] ? (tpl['key'] as string) : role;
  return {
    id: refIdOf(tpl['id'] ?? tpl['_id']),
    surface: String(tpl['surface'] ?? 'web'),
    company: tpl['company'] ? refIdOf(tpl['company']) : null,
    key,
    role,
    system: typeof tpl['system'] === 'boolean' ? (tpl['system'] as boolean) : key === role,
    name: (tpl['name'] as string) ?? null,
    description: (tpl['description'] as string) ?? null,
    icon: (tpl['icon'] as string) ?? null,
    permissions: strings(tpl['permissions']),
    state: tpl['state'] === 'published' ? 'published' : 'draft',
    version: Number(tpl['version'] ?? 0),
    publishedAt: (tpl['publishedAt'] as string) ?? null,
    publishedBy: tpl['publishedBy'] as AccessTemplate['publishedBy'],
    publishedPermissions: strings(tpl['publishedPermissions']),
    active: tpl['active'] !== false,
    updatedAt: tpl['updatedAt'] as string | undefined,
    createdAt: tpl['createdAt'] as string | undefined,
    affectedUsers: affected === undefined ? undefined : count(affected),
  };
}

/** `{ key, name, role, system }` tolerante (sin key = rol del sistema de ese rol base). */
function normalizeRole(raw: Loose, fallbackRole?: unknown): CompanyRole | null {
  const role = raw['role'] ?? raw['baseRole'] ?? fallbackRole;
  if (!isProductRole(role)) return null;
  const key = typeof raw['key'] === 'string' && raw['key'] ? (raw['key'] as string) : role;
  const name = raw['name'] ?? raw['label'];
  return {
    key,
    name: typeof name === 'string' && name.trim() ? name.trim() : null,
    role,
    system: typeof raw['system'] === 'boolean' ? (raw['system'] as boolean) : key === role,
  };
}

/** Sistema primero en el orden fijo de los roles base; luego los propios por nombre. */
export function sortRoles<T extends CompanyRole>(roles: T[]): T[] {
  const rank = (role: T) => (role.system ? PRODUCT_ROLES.indexOf(role.role) : PRODUCT_ROLES.length);
  const nameOf = (role: T) => role.name || PRODUCT_ROLE_LABEL[role.role];
  return [...roles].sort((a, b) => rank(a) - rank(b) || nameOf(a).localeCompare(nameOf(b), 'es'));
}

/**
 * Filas de roles (§8.4). Acepta `[{ key, name, role, system, template,
 * affectedUsers }]`, la forma anterior `[{ role, label, template }]`, un
 * arreglo de plantillas o un objeto `{ [role]: plantilla }`. Siempre devuelve
 * los 5 roles del sistema (con `template: null` si falta) y, con
 * `withCustom`, los propios después.
 */
function normalizeRoleRows(raw: unknown, withCustom: boolean): CompanyTemplateRow[] {
  const byKey = new Map<string, CompanyTemplateRow>();
  const push = (obj: Loose, template: unknown, fallbackRole?: unknown) => {
    const tpl = template ? normalizeTemplate(template) : null;
    const tplLoose = (template ?? {}) as Loose;
    const role = normalizeRole(
      {
        key: obj['key'] ?? tplLoose['key'],
        name: obj['name'] ?? tpl?.name ?? obj['label'],
        role: obj['role'] ?? tplLoose['role'],
        system: obj['system'] ?? tplLoose['system'],
      },
      fallbackRole,
    );
    if (!role) return;
    if (!role.system && !withCustom) return;
    byKey.set(role.key, {
      ...role,
      template: tpl,
      affectedUsers: count(obj['affectedUsers'] ?? tpl?.affectedUsers),
    });
  };
  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const obj = (item ?? {}) as Loose;
      push(obj, 'template' in obj ? obj['template'] : obj);
    });
  } else if (raw && typeof raw === 'object') {
    Object.entries(raw as Loose).forEach(([role, value]) => {
      const obj = (value ?? {}) as Loose;
      push(obj, 'template' in obj ? obj['template'] : value, role);
    });
  }
  const system = PRODUCT_ROLES.map(
    (role): CompanyTemplateRow =>
      byKey.get(role) ?? {
        key: role,
        name: null,
        role,
        system: true,
        template: null,
        affectedUsers: 0,
      },
  );
  const custom = [...byKey.values()].filter((row) => !row.system);
  return [...system, ...sortRoles(custom)];
}

function normalizeBreakdownRow(row: ApplyBreakdownRow): ApplyBreakdownRow {
  return {
    ...row,
    companyId: refIdOf(row.companyId),
    name: row.name ?? null,
    affectedUsers: count(row.affectedUsers),
  };
}

function normalizeUserAccess(raw: UserAccess | null | undefined): UserAccess {
  const template = raw?.template
    ? {
        id: refIdOf(
          (raw.template as unknown as Loose)['id'] ?? (raw.template as unknown as Loose)['_id'],
        ),
        version: Number(raw.template.version ?? 0),
        state: raw.template.state,
        permissions: strings(raw.template.permissions),
      }
    : null;
  // §8.4: `roles` son los ROLES DE LA EMPRESA (objetos) y los roles base de
  // la persona vienen en `baseRoles`. La forma anterior (`roles` como strings
  // = roles base) se sigue aceptando.
  const loose = (raw ?? {}) as unknown as Loose;
  const rawRoles = Array.isArray(loose['roles']) ? (loose['roles'] as unknown[]) : [];
  const availableRoles = new Map<string, CompanyRole>();
  rawRoles
    .filter((item) => item && typeof item === 'object')
    .forEach((item) => {
      const role = normalizeRole(item as Loose);
      if (role) availableRoles.set(role.key, role);
    });
  const baseRoles = [...strings(loose['baseRoles']), ...strings(rawRoles)];
  const role = raw?.role ?? null;
  const applied = (loose['accessRole'] ?? null) as Loose | null;
  return {
    role,
    roles: baseRoles.length ? [...new Set(baseRoles)] : role ? [role] : [],
    roleKey:
      typeof loose['roleKey'] === 'string' && loose['roleKey']
        ? (loose['roleKey'] as string)
        : null,
    accessRole:
      applied && typeof applied['key'] === 'string'
        ? {
            key: applied['key'] as string,
            name: typeof applied['name'] === 'string' ? (applied['name'] as string) : null,
            system: Boolean(applied['system']),
            baseRole: String(applied['baseRole'] ?? applied['role'] ?? role ?? ''),
          }
        : null,
    availableRoles: sortRoles([...availableRoles.values()]),
    template,
    granted: strings(raw?.granted),
    revoked: strings(raw?.revoked),
    disabledModules: strings(raw?.disabledModules),
    effective: strings(raw?.effective),
  };
}
